/**
 * The timing of the intro slideshow, stated once.
 *
 * The whole loop is CSS keyframes and nothing else: no timer, no state, no
 * React re-render per slide, and no library. The band is prerendered, so a
 * JavaScript-driven slideshow would ship one slide in the HTML and swap it
 * after hydration — a visible cut on every cold load. Keyframes start the
 * moment the stylesheet lands and keep running whether the bundle arrives or
 * not.
 *
 * Only `opacity` and `transform` are animated, so every frame stays on the
 * compositor and none of them can trigger layout (§52).
 */

/** How long a car holds the stage, before the fade to the next one begins. */
const HOLD_SECONDS = 6;

/**
 * The cross-fade. Long enough that one car dissolves into the next instead of
 * cutting, short enough that the pair are only both present for a moment —
 * two cut-outs at half opacity read as one car with two grilles, which is the
 * same trap the home hero's carousel had to design around.
 */
const FADE_SECONDS = 1.2;

export type SlideMotion = {
  /** `animation` for the fading layer. */
  fade: string;
  /** `animation` for the drifting layer inside it. */
  drift: string;
  /**
   * The layer's resting opacity — what the prerendered HTML paints, what a
   * reduced-motion visitor keeps, and what shows during a slide's delay.
   * Only the first car rests visible; the rest rest at zero.
   */
  restingOpacity: number;
};

export const INTRO_FADE_KEYFRAMES = 'kabex-intro-fade';
export const INTRO_DRIFT_KEYFRAMES = 'kabex-intro-drift';

/**
 * The two keyframe sets, as `sx` entries on whatever wraps the slides.
 *
 * `count` decides the cycle, so adding or removing a car needs no other
 * change: the stops are percentages of a loop that grows with the set.
 *
 * The drift is monotonic rather than a there-and-back, because a car that
 * eases back to where it started reads as a rocking photograph. It runs from
 * slightly wide and right to slightly close and left across the whole cycle,
 * and the jump back to the start lands at 0%, where the layer is transparent.
 */
export const introSlideshowKeyframesSx = (count: number) => {
  const cycle = count * HOLD_SECONDS;
  const stop = (seconds: number) => `${((seconds / cycle) * 100).toFixed(3)}%`;

  return {
    [`@keyframes ${INTRO_FADE_KEYFRAMES}`]: {
      '0%': { opacity: 0 },
      [stop(FADE_SECONDS)]: { opacity: 1 },
      [stop(HOLD_SECONDS)]: { opacity: 1 },
      [stop(HOLD_SECONDS + FADE_SECONDS)]: { opacity: 0 },
      '100%': { opacity: 0 },
    },
    [`@keyframes ${INTRO_DRIFT_KEYFRAMES}`]: {
      from: { transform: 'scale(1.04) translate3d(1.6%, 0, 0)' },
      to: { transform: 'scale(1.12) translate3d(-1.6%, 0, 0)' },
    },
  };
};

/**
 * One slide's share of the loop.
 *
 * The first car carries a negative delay of exactly one cross-fade, which puts
 * it at the end of its own fade-in at time zero. That is what lets the band
 * open on a car that is already there — start it at 0% instead and the page
 * loads to an empty stage that fades in, which looks like something failing to
 * arrive rather than a film beginning.
 */
export const slideMotion = (index: number, count: number): SlideMotion => {
  const cycle = count * HOLD_SECONDS;
  const delay = index * HOLD_SECONDS - FADE_SECONDS;
  const timing = `${cycle}s linear ${delay}s infinite both`;

  return {
    fade: `${INTRO_FADE_KEYFRAMES} ${timing}`,
    drift: `${INTRO_DRIFT_KEYFRAMES} ${timing}`,
    restingOpacity: index === 0 ? 1 : 0,
  };
};
